<template>
  <div class="flex flex-col gap-3 pb-4">
    <!-- Loading -->
    <template v-if="loading">
      <div v-for="i in 4" :key="i" class="flex gap-3 items-center px-1">
        <Skeleton width="3rem" height="1rem" borderRadius="0.5rem" />
        <Skeleton width="100%" height="3.5rem" borderRadius="0.75rem" />
      </div>
    </template>

    <!-- Error -->
    <div v-else-if="error" class="flex flex-col items-center gap-3 py-10 text-center">
      <i class="pi pi-exclamation-circle text-2xl text-gray-300" />
      <span class="text-sm text-gray-500">לא הצלחנו לטעון את התורים הממתינים לאישור</span>
      <button
        type="button"
        class="px-4 py-1.5 rounded-full border border-primary-200 text-primary bg-primary-50 text-[13px] font-medium"
        @click="emit('retry')"
      >נסו שוב</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!dayGroups.length" class="flex flex-col items-center gap-2 py-12 text-center">
      <i class="pi pi-check-circle text-3xl text-green-400" />
      <span class="text-sm font-medium">אין תורים הממתינים לאישור</span>
      <span class="text-xs text-gray-500 px-8">כל הבקשות טופלו — תורים חדשים שיוזמנו יופיעו כאן</span>
    </div>

    <template v-else>
      <div class="flex items-center gap-2 px-1">
        <span class="text-[11px] font-semibold text-yellow-700 bg-yellow-100 rounded-full px-2.5 py-1">
          {{ events.length }} ממתינים לאישור
        </span>
        <span class="flex-1" />
        <button
          type="button"
          class="flex items-center gap-1.5 text-[11.5px] text-gray-500"
          @click="emit('retry')"
        >
          <i class="pi pi-refresh !text-[11px]" />
          רענון
        </button>
      </div>

      <div v-for="group in dayGroups" :key="group.iso" class="flex flex-col gap-1.5">
        <!-- Day header + jump to that day in the calendar -->
        <div class="flex items-center gap-2 px-1">
          <span class="text-[12.5px] font-semibold">{{ group.label }}</span>
          <span class="text-[11px] text-gray-400">{{ group.dateLabel }}</span>
          <span class="flex-1" />
          <span class="text-[11px] text-gray-500">{{ group.items.length }} תורים</span>
        </div>

        <div
          v-for="item in group.items"
          :key="`${item.event.id}-${item.event.start}`"
          class="bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex items-center gap-2.5 cursor-pointer active:bg-gray-50 hover:bg-gray-50 transition-colors"
          @click="emit('event', item.event)"
        >
          <div class="w-[44px] flex-none text-center" style="direction: ltr">
            <div class="text-[12.5px] font-semibold leading-tight">{{ item.startLabel }}</div>
            <div class="text-[10px] text-gray-400 leading-tight">{{ item.endLabel }}</div>
          </div>
          <div class="w-[3px] self-stretch rounded-full flex-none" :style="{ background: item.color }" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1 min-w-0">
              <i
                v-if="item.event.customerHasWarning"
                class="pi pi-exclamation-triangle text-red-600 flex-none"
                style="font-size: 0.7rem"
                title="ללקוח זה יש אזהרה"
              />
              <span class="text-[13px] font-medium truncate">{{ item.title }}</span>
            </div>
            <div v-if="item.subtitle" class="text-[11px] text-gray-500 truncate">
              {{ item.subtitle }}
            </div>
          </div>

          <!-- Small jump-to-calendar button — opens this appointment's day in the daily view -->
          <button
            type="button"
            class="flex-none flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-50 text-primary text-[11px] font-medium"
            title="מעבר ליום בלוח"
            @click.stop="emit('pick-day', item.jsDate)"
          >
            <i class="pi pi-calendar !text-[11px]" />
            <span class="hidden sm:inline">ליום בלוח</span>
          </button>
          <i class="pi pi-chevron-left !text-[11px] text-gray-300 flex-none" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AppointmentType, type ICalendarEvent } from '@torim-app/types';
import { DateTime } from 'luxon';
import Skeleton from 'primevue/skeleton';

const props = defineProps<{
  events: ICalendarEvent[];
  loading: boolean;
  error: boolean;
  // Show the employee's name on each row when several employees are in scope.
  multiStaff: boolean;
  staffNameOf: (e: ICalendarEvent) => string;
  colorOf: (e: ICalendarEvent) => { wrapperBackground: string; lineBg: string; border?: string } | null;
}>();
const emit = defineEmits<{
  event: [e: ICalendarEvent];
  'pick-day': [d: Date];
  retry: [];
}>();

const ZONE = 'Asia/Jerusalem';
const HEBREW_WEEKDAYS_FULL = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const parse = (s?: string): DateTime | null => {
  if (!s) return null;
  const dt = DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm', { zone: ZONE });
  return dt.isValid ? dt : null;
};

const isManual = (e: ICalendarEvent) => e.type === AppointmentType.MANUAL || e.type === AppointmentType.OUT_OF_OFFICE;

interface IPendingRow {
  event: ICalendarEvent;
  startLabel: string;
  endLabel: string;
  title: string;
  subtitle: string;
  color: string;
  jsDate: Date;
}
interface IPendingDayGroup {
  iso: string;
  label: string;
  dateLabel: string;
  jsDate: Date;
  items: IPendingRow[];
}

const dayGroups = computed<IPendingDayGroup[]>(() => {
  const today = DateTime.local({ zone: ZONE }).startOf('day');
  const groups = new Map<string, IPendingDayGroup>();

  for (const event of props.events) {
    const start = parse(event.start);
    if (!start) continue;
    const end = parse(event.end);
    const iso = start.toISODate()!;

    if (!groups.has(iso)) {
      const dayStart = start.startOf('day');
      const diff = Math.round(dayStart.diff(today, 'days').days);
      const weekday = HEBREW_WEEKDAYS_FULL[dayStart.weekday % 7];
      const label = diff === 0 ? 'היום' : diff === 1 ? 'מחר' : `יום ${weekday}`;
      groups.set(iso, {
        iso,
        label,
        dateLabel: `${dayStart.day} ב${HEBREW_MONTHS[dayStart.month - 1]}${diff <= 1 ? ` · יום ${weekday}` : ''}`,
        jsDate: dayStart.toJSDate(),
        items: []
      });
    }

    const customer = event.customerName ?? '';
    const service = event.serviceName ?? event.title;
    const staff = props.multiStaff ? props.staffNameOf(event) : '';
    const subtitleParts = [isManual(event) ? '' : service, staff].filter(Boolean);

    groups.get(iso)!.items.push({
      event,
      startLabel: start.toFormat('HH:mm'),
      endLabel: end ? end.toFormat('HH:mm') : '',
      title: isManual(event) ? event.title : (customer || event.title),
      subtitle: subtitleParts.join(' · '),
      color: props.colorOf(event)?.lineBg ?? '#8864ee',
      jsDate: groups.get(iso)!.jsDate
    });
  }

  return [...groups.values()].sort((a, b) => a.iso.localeCompare(b.iso));
});
</script>
